"use client";

import { useMemo, useState } from "react";

import type { LinkTarget } from "@/components/link";
import type { ListingMap } from "@/hooks/use-listing-counts";
import type { RouterOutputs } from "@/types/rpc";

import { AlbumResults } from "./album-results";
import { ArtistResults } from "./artist-results";
import { mergeRelatedSearchEntities } from "./merge-related-search-entities";
import { RelatedSearchEntities } from "./related-search-entities";
import { SearchEmptyPlaceholder } from "./search-empty-placeholder";

type Album = RouterOutputs["music"]["searchAlbums"]["albums"][number];
type Artist = RouterOutputs["music"]["searchArtists"]["artists"][number];
type CompanyMatch = RouterOutputs["music"]["searchCompanies"][number];

/**
 * Composes the music-tab result sections. Albums and artists are independent
 * legs — each renders its own loading/error/empty so neither blocks the other.
 * The combined "no results" placeholder only shows once both legs have settled.
 */
export function MusicSearchResults({
  artists,
  artistsLoading,
  artistsError,
  albums,
  albumsLoading,
  albumsError,
  labels,
  companies,
  listingMap,
  buildAlbumLink,
  hasMore,
  page,
  preferAlbumsFirst,
  query,
  updateFilter,
}: {
  artists: Artist[];
  artistsLoading: boolean;
  artistsError?: string;
  albums: Album[];
  albumsLoading: boolean;
  albumsError?: string;
  /** Top label name matches for the text query (compact links only). */
  labels: CompanyMatch[];
  /** Top company name matches for the text query (compact links only). */
  companies: CompanyMatch[];
  listingMap: ListingMap;
  buildAlbumLink: (album: Album) => LinkTarget;
  hasMore: boolean;
  page: number;
  preferAlbumsFirst: boolean;
  query: string;
  updateFilter: (key: string, value: string | number | boolean | undefined) => void;
}) {
  const [showAllRelatedEntities, setShowAllRelatedEntities] = useState(false);
  const relatedEntities = useMemo(
    () => mergeRelatedSearchEntities(labels, companies),
    [labels, companies],
  );
  const settled = !artistsLoading && !albumsLoading;
  const showEmptyPlaceholder =
    settled &&
    !artistsError &&
    !albumsError &&
    artists.length === 0 &&
    albums.length === 0 &&
    relatedEntities.length === 0;
  const artistResults = (
    <ArtistResults artists={artists} isLoading={artistsLoading} error={artistsError} />
  );
  const albumResults = (
    <AlbumResults
      albums={albums}
      isLoading={albumsLoading}
      error={albumsError}
      listingMap={listingMap}
      buildAlbumLink={buildAlbumLink}
      hasMore={hasMore}
      page={page}
      updateFilter={updateFilter}
    />
  );

  return (
    <>
      {preferAlbumsFirst ? (
        <>
          {albumResults}
          {artistResults}
        </>
      ) : (
        <>
          {artistResults}
          {albumResults}
        </>
      )}
      <RelatedSearchEntities
        entities={relatedEntities}
        expanded={showAllRelatedEntities}
        onToggle={() => setShowAllRelatedEntities((value) => !value)}
      />
      <SearchEmptyPlaceholder show={showEmptyPlaceholder} query={query} />
    </>
  );
}
